<!--
  BalanceReport.vue
  ---------------------------------------------------------------------------
  Generic "search card + table card + pagination + footer totals" component.

  What's dynamic / reusable:
    - `fields`        -> passed straight to <n-table>
    - `search-fields`  -> passed straight to <n-search-input>
    - all cell/foot/header rendering -> forwarded via scoped-slot passthrough,
      so each page decides how its own columns look (currency formatting,
      links, badges, outstanding breakdowns, etc.) without this component
      needing to know about "commission", "netWl", "outstanding1"...

  This component owns NO business/domain logic (no repository calls, no
  $can() checks, no modals). It only owns the generic UI shell + events.
-->
<template>
  <div>
    <!-- Search Container Card -->
    <n-search-container
      v-if="searchFields.length"
      :active="true"
      :show-header="false"
      show-search
      :loading="loading"
      @search="$emit('search', $event)"
      @reset="$emit('reset')"
    >
      <n-search-input
        ref="search"
        v-model="innerParams"
        :fields="searchFields"
      >
        <template #typeId="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
        <template
          v-for="slotName in searchSlotNames"
          #[slotName]="scope"
        >
          <slot
            :name="slotName"
            v-bind="scope"
          />
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-0-5">
        <b-row
          v-if="showBackButton || showRefreshButton"
          class="mb-2"
        >
          <b-col
            v-if="showBackButton"
            cols="6"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('back')"
            >
              <feather-icon icon="ChevronLeftIcon" />
              {{ $t('button.back') }}
            </b-button>
          </b-col>
          <b-col
            v-if="showRefreshButton"
            :cols="showBackButton ? 6 : 12"
            class="text-right"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('refresh')"
            >
              <feather-icon icon="RefreshCcwIcon" />
              {{ $t('button.refresh') }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              :value="perPage"
              :dir="isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
              @input="$emit('update:perPage', $event)"
            />
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="perPage"
          :total="total"
          @sort-changed="(...args) => $emit('sort-changed', ...args)"
        >
          <template #cell(isVendor)="{ item }">
            <b-badge
              v-if="item.isVendor"
              pill
              variant="info"
              badge-glow
            >
              {{ $t('general.external') }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="primary"
              badge-glow
            >
              {{ $t('general.internal') }}
            </b-badge>
          </template>

          <template #cell(username)="{ item }">
            <b-link
              v-if="typeId !== 5 && user.typeId !== 5"
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.username }}
            </b-link>
            <span v-else>{{ item.username }}</span>
            <small
              v-if="item.secondUsername"
              class="d-block text-wala"
            >
              ({{ item.secondUsername }})
            </small>
          </template>

          <template #cell(turnover)="{ item }">
            <span>{{ item.turnover | currency(item.ccy) }}</span>
          </template>

          <template #cell(commission)="{ item }">
            <span class="text-wala">{{ item.commission | currency(item.ccy) }}</span>
            <div
              v-if="canViewAccrue"
              class="text-draw"
            >
              ({{ item.accrueCommission | currency(item.ccy) }})
            </div>
            <div
              v-if="canViewPayback"
              class="text-brand"
            >
              ({{ item.paybackCommission | currency(item.ccy) }})
            </div>
          </template>

          <template #cell(netWl)="{ item }">
            <b-link
              class="link-underline"
              :class="{ 'text-wala': item.netWl >= 0, 'text-meron': item.netWl < 0 }"
              @click="openBetHistory(item)"
            >
              {{ item.netWl | currency(item.ccy) }}
            </b-link>
            <div v-if="canViewJackpot">
              <b-link
                class="link-underline text-draw"
                @click="openJackpotHistory(item)"
              >
                {{ item[jackpotField] | currency(item.ccy) }}
              </b-link>
            </div>
          </template>

          <template #cell(newWl)="{ item }">
            <b-link
              class="link-underline"
              :class="{ 'text-wala': item.newWl >= 0, 'text-meron': item.newWl < 0 }"
              @click="openBetHistory(item)"
            >
              {{ item.newWl | currency(item.ccy) }}
            </b-link>
            <div v-if="canViewJackpot">
              <b-link
                class="link-underline text-draw"
                @click="openJackpotHistory(item)"
              >
                {{ item[jackpotField] | currency(item.ccy) }}
              </b-link>
            </div>
          </template>

          <template #cell(deposit)="{ item }">
            <b-link
              class="link-underline text-wala"
              @click="openTransactionHistory(item.userId, item.username, 'deposit')"
            >
              {{ item.deposit | currency(item.ccy) }}
            </b-link>
            <template v-if="depositPromo">
              <br>
              <b-link
                class="link-underline text-draw"
                @click="openPromotionHistory(item)"
              >
                ({{ item.promoAmount | currency(item.ccy) }})
              </b-link>
            </template>
            <template v-if="canViewDepositWithdrawalPlayer">
              <br>
              ({{ item.depositPlayer | currency(item.ccy) }})
            </template>
          </template>

          <template #cell(withdrawal)="{ item }">
            <b-link
              class="link-underline text-meron"
              @click="openTransactionHistory(item.userId, item.username, 'withdrawal')"
            >
              {{ item.withdrawal | currency(item.ccy) }}
            </b-link>
            <template v-if="canViewDepositWithdrawalPlayer">
              <br>
              ({{ item.withdrawalPlayer | currency(item.ccy) }})
            </template>
          </template>

          <template #cell(balance)="{ item }">
            {{ item.balance | currency(item.ccy) }}
          </template>

          <template
            v-for="outstandingField in outstandingFields"
            #[`cell(${outstandingField})`]="{ item }"
          >
            <span
              v-if="isObject(item[`${outstandingField}`])"
              :key="outstandingField"
            >
              <span class="text-meron">
                {{ item[`${outstandingField}`].meronBetTotal | currency(item.ccy) }}
              </span>
              |
              <span class="text-wala">
                {{ item[`${outstandingField}`].walaBetTotal | currency(item.ccy) }}
              </span>
              <br>
              <span class="text-draw">
                {{ item[`${outstandingField}`].drawBetTotal | currency(item.ccy) }}
              </span>
            </span>
          </template>

          <template
            v-for="otherOutstandingField in otherOutstandingFields"
            #[`cell(${otherOutstandingField})`]="{ item }"
          >
            <span
              v-if="isObject(item)"
              :key="otherOutstandingField"
            >
              {{ item[`${otherOutstandingField}`] | currency(item.ccy) }}
            </span>
          </template>

          <template #cell(member)="{ item }">
            <b-link
              v-if="typeId !== 5 && user.typeId !== 5"
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.member }}
            </b-link>
            <span v-else>{{ item.member }}</span>
            <div
              v-if="showActivePlayer && $can('update', 'bet-setting')"
              class="text-success"
            >
              ({{ item.activePlayer }})
            </div>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              v-if="item.status"
              pill
              variant="success"
              badge-glow
            >
              {{ $t('status.active') }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t('status.inactive') }}
            </b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <div class="text-nowrap">
              <b-button
                v-if="$can('update', getTypeById(item.typeId)) && getTypeById(item.typeId)"
                v-b-tooltip.hover
                :to="{ name: `update-${getTypeById(item.typeId)}`, params: { id: item.userId } }"
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="($can('create', 'deposit-withdrawal') || $can('edit', 'deposit-withdrawal')) && !item.isVendor"
                v-b-tooltip.hover
                :title="$t('button.deposit')"
                variant="primary"
                pill
                size="sm"
                @click="showDepositOrWithdrawModel(item.userId, item.username, item.balance, item.ccy)"
              >
                <feather-icon icon="CreditCardIcon" />
              </b-button>
              <b-button
                v-if="($can('create', 'deposit-withdrawal') || $can('edit', 'deposit-withdrawal')) && !item.isVendor"
                v-b-tooltip.hover
                :title="$t('button.withdraw')"
                variant="primary"
                pill
                size="sm"
                @click="showDepositOrWithdrawModel(item.userId, item.username, item.balance, item.ccy, 'withdrawal')"
              >
                <feather-icon icon="RotateCcwIcon" />
              </b-button>
              <b-button
                v-if="item.typeId === 6"
                v-b-tooltip.hover
                :title="$t('button.betHistory')"
                variant="primary"
                pill
                size="sm"
                @click="openBetHistory(item)"
              >
                <feather-icon icon="BookOpenIcon" />
              </b-button>
            </div>
          </template>

          <template
            v-if="headerLabel"
            #thead-top=""
          >
            <b-tr class="text-center">
              <b-th
                :key="fields.length"
                :colspan="fields.length"
              >
                {{ headerLabel }}
              </b-th>
            </b-tr>
          </template>

          <!-- Forward every cell(...) / custom-foot slot supplied by the parent-->
          <template
            v-for="slotName in tableSlotNames"
            #[slotName]="scope"
          >
            <slot
              :name="slotName"
              v-bind="scope"
            />
          </template>

          <template #custom-foot>
            <b-tr v-if="!loading">
              <b-th colspan="3" />
              <b-th class="text-right">
                <h5 class="font-weight-bold">
                  {{ totalReport.turnover | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{ 'text-wala': totalReport.netWl >= 0, 'text-meron': totalReport.netWl < 0 }"
                >
                  {{ totalReport.netWl | currency(totalReport.ccy) }}
                </h5>
                <h5
                  v-if="canViewJackpot && isObject(totalReport)"
                  class="text-draw"
                >
                  <b-link
                    v-if="clickableTotalJackpot"
                    class="text-draw link-underline"
                    @click="openJackpotHistory()"
                  >
                    {{ totalReport[jackpotField] | currency(totalReport.ccy) }}
                  </b-link>
                  <template v-else>
                    {{ totalReport[jackpotField] | currency(totalReport.ccy) }}
                  </template>
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5 class="font-weight-bold text-wala">
                  {{ totalReport.commission | currency(totalReport.ccy) }}
                </h5>
                <h5
                  v-if="canViewAccrue"
                  class="font-weight-bold text-draw"
                >
                  ({{ totalReport.accrueCommission | currency(totalReport.ccy) }})
                </h5>
                <h5
                  v-if="canViewPayback"
                  class="font-weight-bold text-brand"
                >
                  ({{ totalReport.paybackCommission | currency(totalReport.ccy) }})
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{ 'text-wala': totalReport.newWl >= 0, 'text-meron': totalReport.newWl < 0 }"
                >
                  {{ totalReport.newWl | currency(totalReport.ccy) }}
                </h5>
                <h5
                  v-if="canViewJackpot && isObject(totalReport)"
                  class="text-draw"
                >
                  <b-link
                    v-if="clickableTotalJackpot"
                    class="text-draw link-underline"
                    @click="openJackpotHistory()"
                  >
                    {{ totalReport[jackpotField] | currency(totalReport.ccy) }}
                  </b-link>
                  <template v-else>
                    {{ totalReport[jackpotField] | currency(totalReport.ccy) }}
                  </template>
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{ 'text-wala': totalReport.deposit >= 0 }"
                >
                  {{ totalReport.deposit | currency(totalReport.ccy) }}
                </h5>
                <h5
                  v-if="depositPromo"
                  class="font-weight-bold text-draw"
                >
                  ({{ totalReport.promoAmount | currency(totalReport.ccy) }})
                </h5>
                <h5
                  v-if="canViewDepositWithdrawalPlayer"
                  class="font-weight-bold"
                >
                  ({{ totalReport.depositPlayer | currency(totalReport.ccy) }})
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{ 'text-meron': totalReport.withdrawal >= 0 }"
                >
                  {{ totalReport.withdrawal | currency(totalReport.ccy) }}
                </h5>
                <h5
                  v-if="canViewDepositWithdrawalPlayer"
                  class="font-weight-bold"
                >
                  ({{ totalReport.withdrawalPlayer | currency(totalReport.ccy) }})
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5 class="text-brand">
                  {{ totalReport.balance | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th
                v-if="typeId !== 5 && user.typeId !== 5"
                class="text-center font-weight-bold"
              >
                <h5>
                  {{ totalReport.member }}
                </h5>
                <div
                  v-if="showActivePlayer && $can('update', 'bet-setting')"
                  class="text-success"
                >
                  ({{ totalReport.activePlayer }})
                </div>
              </b-th>
              <b-th
                v-for="outstandingField in outstandingFields"
                :key="outstandingField"
                class="text-center"
              >
                <h5
                  v-if="isObject(totalReport[`${outstandingField}`])"
                  class="font-weight-bold"
                >
                  <span class="text-meron">
                    {{ totalReport[`${outstandingField}`].meronBetTotal | currency(totalReport.ccy) }}
                  </span>
                  |
                  <span class="text-wala">
                    {{ totalReport[`${outstandingField}`].walaBetTotal | currency(totalReport.ccy) }}
                  </span>
                  <br>
                  <span class="text-draw">
                    {{ totalReport[`${outstandingField}`].drawBetTotal | currency(totalReport.ccy) }}
                  </span>
                </h5>
              </b-th>
              <b-th
                v-for="otherOutstandingField in otherOutstandingFields"
                :key="otherOutstandingField"
                class="text-center"
              >
                <h5
                  v-if="isObject(totalReport)"
                  class="text-center font-weight-bold"
                >
                  {{ totalReport[`${otherOutstandingField}`] | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th colspan="1" />
              <b-th colspan="1" />
              <b-th
                v-if="typeId === 5 || user.typeId === 5"
                colspan="1"
              />
            </b-tr>
          </template>
        </n-table>

        <n-pagination
          ref="pagination"
          class="mt-2"
          :total="total"
          :per-page="perPage"
          :page="params.page"
          @change="$emit('page-change', $event)"
        />
      </div>
    </b-card>

    <deposit-or-withdraw
      ref="depositWithdrawModel"
      @success="depositOrWithdrawSuccess"
    />
    <bet-history ref="betHistoryModal" />
    <jackpot-history ref="jackpotHistoryModal" />
    <transaction-history ref="transactionHistoryModal" />
    <promotion-history ref="promotionHistoryModal" />
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormRadio,
  BFormRadioGroup,
  BLink,
  BRow,
  BTh,
  BTr,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import DepositOrWithdraw from '@/views/account/depositOrWithdraw.vue'
import BetHistory from '@/views/balanceReport/betHistory/List.vue'
import JackpotHistory from '@/views/lottoBalanceReport/jackpotHistory/List.vue'
import TransactionHistory from '@/views/balanceReport/transactionHistory/List.vue'
import PromotionHistory from '@/views/balanceReport/promotionHistory/List.vue'

export default {
  name: 'BalanceReport',
  components: {
    PromotionHistory,
    TransactionHistory,
    JackpotHistory,
    BetHistory,
    DepositOrWithdraw,
    BLink,
    BBadge,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BButton,
    BTr,
    BTh,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    channelType: Number,
    // eslint-disable-next-line vue/require-default-prop
    reportTypeId: Number,
    isVvip: Boolean,
    clickableTotalJackpot: Boolean,
    showJackpot: Boolean,
    jackpotField: {
      type: String,
      default: 'jackpotAmount',
    },
    showPromoAmount: Boolean,
    showAccrue: Boolean,
    showPayback: Boolean,
    forceShowDepositWithdrawalPlayer: Boolean,
    showDepositWithdrawalPlayer: Boolean,
    showActivePlayer: Boolean,
    outstandingFields: {
      type: Array,
      default: () => [],
    },
    otherOutstandingFields: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      required: true,
    },
    searchFields: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalReport: {
      type: Object,
      default: () => {},
    },
    params: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => {
      },
    },
    loading: Boolean,
    total: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: [Number, String],
      required: true,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    isRTL: Boolean,
    headerLabel: {
      type: String,
      default: '',
    },
    showBackButton: Boolean,
    showRefreshButton: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // v-model passthrough into <n-search-input>, keeps parent's `params`
    // object reactive without this component needing to know its shape.
    innerParams: {
      get() {
        return this.params
      },
      set(value) {
        this.$emit('update:params', value)
      },
    },
    // Any scoped slot the parent passes named "typeId", "period", etc.
    // (i.e. matching a search field key) goes to n-search-input.
    searchSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => searchKeys.includes(name))
    },
    // Everything else (cell(xxx), custom-foot, thead-top overrides, etc.)
    // is forwarded to n-table.
    tableSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => !searchKeys.includes(name))
    },

    userData() {
      return this.$store.state.profile.name
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem('userData'))
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId
      }
      return this.userData?.typeId
    },
    canViewDepositWithdrawalPlayer() {
      if (this.forceShowDepositWithdrawalPlayer) return true
      if (!this.showDepositWithdrawalPlayer) return false
      if (this.user.typeId !== 1 && this.user.typeId !== 4) return false
      return this.$can('read', 'deposit-withdrawal') && this.userData.typeId === 1
    },
    canViewPayback() {
      if (!this.showPayback) return false
      if (this.user.typeId < 4) return false
      return this.$can('update', 'bet-setting') || this.userData.isPayback
    },
    canViewAccrue() {
      if (!this.showAccrue) return false
      if (this.user.typeId < 4) return false
      return this.$can('update', 'bet-setting') || this.userData.isAccrue
    },
    canViewJackpot() {
      if (!this.showJackpot) return false
      return this.$can('update', 'bet-setting') || this.userData.canPlayLotto || this.userData.vvipAgent
    },
    depositPromo() {
      if (!this.showPromoAmount) return false
      if (this.user.typeId !== 5) return false
      if (this.$store.state.profile.depositPromo) {
        return this.$store.state.profile.depositPromo || this.$can('update', 'bet-setting')
      }
      const userData = JSON.parse(localStorage.getItem('userData'))
      return userData?.depositPromo || this.$can('update', 'bet-setting')
    },
  },

  methods: {
    isObject(value) {
      return value !== null && typeof value === 'object'
    },
    getTypeById(type) {
      const map = {
        2: 'company',
        3: 'senior',
        4: 'master',
        5: 'agent',
        6: 'player',
      }
      return map[type] || ''
    },
    viewMember(userId) {
      this.$emit('viewMember', userId)
    },
    openBetHistory(item) {
      this.$refs.betHistoryModal.show({
        page: 1,
        order: null,
        sort: null,
        userId: item.userId,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        period: this.params.period,
        channelId: this.params.channelId,
        channelType: this.channelType,
        portfolio: this.params.portfolio,
      })
    },
    openJackpotHistory(item = null) {
      this.$refs.jackpotHistoryModal.show({
        page: 1,
        order: null,
        sort: null,
        userId: item != null ? item.userId : this.params.userId,
        typeId: this.reportTypeId,
        vvip: this.isVvip,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        period: this.params.period,
        channelType: this.channelType,
        gameCode: this.channelType,
        // channelId: this.params.channelId,
      })
    },
    openTransactionHistory(userId, username, stateType) {
      this.$refs.transactionHistoryModal.changeData(userId, username, {
        page: 1,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        stateType,
        period: this.params.period,
      })
      this.$refs.transactionHistoryModal.show()
    },
    openPromotionHistory(item) {
      this.$refs.promotionHistoryModal.show({
        page: 1,
        order: null,
        sort: null,
        userId: item.userId,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        period: this.params.period,
      })
    },
    showDepositOrWithdrawModel(userId, username, availableCredit, ccy, type = 'deposit') {
      this.$refs.depositWithdrawModel.changeData({
        type, username, amount: 0, promoAmount: 0, userId, remark: null, availableCredit, ccy,
      })
      this.$refs.depositWithdrawModel.show(`${this.$t('field.deposit')} (${username})`)
    },
    depositOrWithdrawSuccess() {
      this.$emit('depositOrWithdrawSuccess')
    },
  },
}
</script>
